import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import debounce from 'lodash/debounce';
import includes from 'lodash/includes';
import MainContent from '../../MainContent';
import PillBox from '../../../../components/PillBox';
import {
  FilterEngineDetails,
  FilterLocation,
  FilterModels,
  FilterSuggestLocation
} from '../Filter';

import {
  setGenericEvent,
  setSearchTracking
} from '../../../../store/actions/dataLayer';
import {
  getActiveParamsMemo,
  stateFromUrlMatch
} from '../../../../utils/urlHelpers/boats';
import { PortalConfigContext } from '../../../../config/portal';
import { getCurrentLocale } from '../../../../utils/language';

import { scrollToTop } from '../../../../utils/scrollTo';

import './styles.css';
import { definedValue, yieldToMain } from '../../../../utils';
import { getMessages } from '../../../../tppServices/translations/messages';
import { useTPPServices } from '../../../../tppServices/tppDIHooks';
import isEqual from 'lodash/isEqual';

import {
  getLocationFilterConfig,
  getLocationFilterData,
  updateNewFiltersState
} from './FilterHelpers/utilFilters';
import { FilterSearchAlert } from './FilterLayouts/FilterSearchAlert';
import { CollapsibleCondition } from './FilterLayouts/CollapsibleCondition';
import { CollapsibleLength } from './FilterLayouts/CollapsibleLength';
import { CollapsiblePrice } from './FilterLayouts/CollapsiblePrice';
import { CollapsibleYear } from './FilterLayouts/CollapsibleYear';
import { CollapsibleClass } from './FilterLayouts/CollapsibleClass';
import { CollapsibleMakes } from './FilterLayouts/CollapsibleMakes';
import { CollapsibleFuelType } from './FilterLayouts/CollapsibleFuelType';
import { CollapsibleHullMaterial } from './FilterLayouts/CollapsibleHullMaterial';
import { CollapsibleForSaleBy } from './FilterLayouts/CollapsibleForSaleBy';
import { filtersPropType } from './FilterHelpers/FilterPropTypes';

const FiltersSection = (props) => {
  const dispatch = useDispatch();
  const context = useContext(PortalConfigContext);
  // State is huge here, we should define a reducer whith a more granular approach.
  // The size of state here is a bottleneck for the application
  const [state, setState] = useState(() => {
    const initState = {
      initialState: props.initialState || 'open',
      data: stateFromUrlMatch(props?.match?.url, props?.match?.params),
      wasRegionChangedByUser: false
    };
    return initState;
  });
  const [clearLocationTrigger, setClearLocationTrigger] = useState(false);
  const [clearOnlyRadius, setClearOnlyRadius] = useState(false);
  const stateRef = useRef(state);
  const { boatsConstants } = useTPPServices();
  const { formatMessage: t } = useIntl();
  const messages = getMessages();

  useEffect(() => {
    setCurrentState(props?.match?.url, props?.match?.params);
  }, [props?.match?.url]);

  const setCurrentState = (url, urlMatch) => {
    const params = getCurrentState(url, urlMatch);
    setState((prevState) => {
      if (isEqual(prevState.data, params)) {
        return prevState;
      }
      const newState = { ...prevState, data: params };
      return newState;
    });
  };

  const getCurrentState = (url, urlMatch) => {
    const res = stateFromUrlMatch(url, urlMatch);
    return res;
  };

  const updateStateOnChange = useCallback(async (type, value, doDebounce) => {
    if (type === 'location_place_id') {
      if (
        stateRef.current.data.placeId === value.placeId &&
        stateRef.current.data.radius === value.radius &&
        (stateRef.current.data.latlon === '' )
      ) {
        return;
      }
    } else if (type === 'coordinates') {
      if (
        stateRef.current.data.latlon === value.latlon &&
        stateRef.current.data.radius === value.radius &&
        (stateRef.current.data.placeId === '')
      ) {
        return;
      }
    }

    await yieldToMain();
    const lastState = stateRef.current;
    const newState = updateNewFiltersState(lastState, type, value, doDebounce);
    setState(newState);
  }, []);

  const onChangedFilters = async (type, value) => {
    const doDebounce = false;
    await updateStateOnChange(type, value, doDebounce, null);
  };

  const debounceOnDataChange = useCallback(debounce(() => {
    props.onDataChange(state.data);
  }, 500), [props.onDataChange, state.data]);

  useEffect(() => {
    if (isEqual(stateRef.current?.data, state.data)) {
      return;
    }
    stateRef.current = state;
    if (state.doDebounce) {
      debounceOnDataChange();
      return;
    }
    props.onDataChange(state.data);
  }, [state.data, debounceOnDataChange, props.onDataChange]);

  const handleDataChange = async (type, value, caller) => {
    const doDebounce = true;
    await updateStateOnChange(type, value, doDebounce, caller);
  };

  const handleRadiusClear = useCallback(async () => {
    const newState = {};
    newState.radius = '';
    setState((prevState) => {
      const updated = { ...prevState, data: { ...prevState.data, ...newState } };
      return updated;
    });
    setClearLocationTrigger(true);
    setClearOnlyRadius(true);
  }, []);

  const handleDataClear = useCallback(async () => {
    await yieldToMain();
    dispatch(setGenericEvent('site search', 'guided search', 'clear filters'));
    const defaultFilterState = stateFromUrlMatch(); // Get default/empty state
    const newState = {
      data: {
        ...defaultFilterState,      // Spread the defaults
        placeId: '',              // Explicitly clear placeId
        latlon: '',              // Explicitly clear latlon
        radius: defaultFilterState.radius
      }
    };
    setState((prevState) => {
      const updated = { ...prevState, ...newState };
      return updated;
    });

    props.onClearErrors && props.onClearErrors();
    scrollToTop();
    setClearLocationTrigger(true);
  }, [dispatch, props.onClearErrors]); // Ensuring dependencies are correct

  const facetAddedTracker = (facet) => {
    dispatch(setSearchTracking(facet, 'single', 'guided search'));
  };

  const facetRemovedTracker = (facet) => {
    dispatch(setGenericEvent('site search', 'guided search', facet));
  };

  const { params, currentParams } = useMemo(() => {
    const params = getCurrentState(props.match?.url, props.match?.params);
    const currentParams = getActiveParamsMemo(params);
    return { params, currentParams };
  }, [props.match?.params]);

  const tracking = useMemo(
    () => ({
      facetAdded: facetAddedTracker,
      facetRemoved: facetRemovedTracker
    }),
    []
  );

  const { mobileParams, customUom } = props;
  const { data } = state;
  const {
    length,
    year,
    price,
    fuelType,
    hullMaterial,
    multiFacetedBoatTypeClass,
    makeModel
  } = data;
  const facets = definedValue(props.facets, {});
  const seoMakeInfo = props.seoMakeInfo;
  const makes = definedValue(facets?.make, []);
  const models = definedValue(facets?.makeModel, []);
  const classFacets = definedValue(facets?.class, []);
  const fuelTypeFacets = definedValue(facets?.fuelType, []);
  const hullMaterialFacets = definedValue(facets?.hullMaterial, []);
  const facetConfig = context.pages.searchResults.facets;
  const isBranded = props.mode === MainContent.MODES.branded;
  const currentLanguage = getCurrentLocale();

  const radiusSearch = definedValue(context.supports?.radiusSearch, {
    enabled: false,
    brandingV2: false
  });

  const filtersData = getLocationFilterData(data, facets, context);
  // to have any place filter we need the country.
  const isFilterLocationSelected = !!state.data?.country;

  useEffect(() => {
    if (clearLocationTrigger) {
      setClearLocationTrigger(false);
    }
  }, [clearLocationTrigger]);

  useEffect(() => {
    if (clearOnlyRadius) {
      setClearOnlyRadius(false);
    }
  }, [clearOnlyRadius]);

  return (
    <>
      <FilterSearchAlert
        match={props.match}
        active={currentParams}
        makeModel={models}
        seoMakeInfo={seoMakeInfo}
        context={context}
        currentLanguage={currentLanguage}
        position={props.position}
      />
      <PillBox
        customUom={customUom}
        active={mobileParams || currentParams}
        params={stateFromUrlMatch()}
        handleDataChange={handleDataChange}
        onClear={handleDataClear}
        onRadiusClear={handleRadiusClear}
        makeModel={models}
        seoMakeInfo={seoMakeInfo}
        searchType={boatsConstants.SEARCH_TYPES.boats}
      />
      <CollapsibleClass
        facets={classFacets}
        handleDataChange={handleDataChange}
        multiFacetedBoatTypeClass={multiFacetedBoatTypeClass}
        params={params}
        position={props.position}
        tracking={tracking}
        label={t(messages.type)}
      />
      <CollapsibleMakes
        makes={makes}
        makeModels={makeModel}
        position={props.position}
        loading={includes(props.componentWorking, 'models')}
        handleDataChange={handleDataChange}
        params={params}
        tracking={tracking}
        label={t(messages.make)}
      />
      <FilterModels
        makes={makes}
        makeModels={makeModel}
        models={models}
        position={props.position}
        loading={includes(props.componentWorking, 'models')}
        handleDataChange={handleDataChange}
        params={params}
        tracking={tracking}
      />
      <FilterSuggestLocation
        context={context}
        isFilterLocationSelected={isFilterLocationSelected}
        handleDataChange={handleDataChange}
        urlLatLon={currentParams.latlon}
        urlRadius={currentParams.radius}
        urlPlaceId={currentParams.placeId}
        shouldClearDisplay={clearLocationTrigger}
        clearOnlyRadius={clearOnlyRadius}
      />
      <CollapsibleCondition
        label={t(messages.condition)}
        position={props.position}
        condition={data.condition}
        handleDataChange={handleDataChange}
        tracking={tracking}
        params={params}
        facetConfig={facetConfig}
      />
      <CollapsibleLength
        label={t(messages.length)}
        facetConfig={facetConfig}
        position={props.position}
        customUom={customUom}
        cookies={props.cookies}
        min={length.min}
        max={length.max}
        handleDataChange={onChangedFilters}
        isThreeColumnLayout={props.isThreeColumnLayout}
      />
      <CollapsiblePrice
        min={price.min}
        max={price.max}
        label={t(messages.price)}
        position={props.position}
        handleDataChange={onChangedFilters}
        tracking={tracking}
        userCurrency={props.userCurrency}
        isThreeColumnLayout={props.isThreeColumnLayout}
      />
      <CollapsibleYear
        label={t(messages.year)}
        position={props.position}
        min={year.min}
        max={year.max}
        handleDataChange={onChangedFilters}
        isThreeColumnLayout={props.isThreeColumnLayout}
      />
      <FilterEngineDetails
        position={props.position}
        data={data}
        facets={facets}
        params={params}
        handleDataChange={handleDataChange}
        tracking={tracking}
      />
      <CollapsibleFuelType
        label={t(messages.fuelType)}
        position={props.position}
        fuelTypes={fuelTypeFacets}
        handleDataChange={handleDataChange}
        params={params}
        selectedFuelTypes={fuelType}
        tracking={tracking}
        facetConfig={facetConfig}
      />
      <CollapsibleHullMaterial
        handleDataChange={handleDataChange}
        hullTypes={hullMaterialFacets}
        params={params}
        selectedHullTypes={hullMaterial}
        position={props.position}
        facetConfig={facetConfig}
        label={t(messages.hullMaterial)}
      />
      <CollapsibleForSaleBy
        isBranded={isBranded}
        label={t(messages.forSale)}
        facetConfig={facetConfig}
        handleDataChange={handleDataChange}
        selectedForSale={data.forSale}
        params={params}
        tracking={tracking}
        position={props.position}
      />
      <FilterLocation
        position={props.position}
        params={params}
        tracking={tracking}
        radiusSearch={radiusSearch}
        filtersConfig={getLocationFilterConfig(facetConfig, tracking)}
        filtersData={filtersData}
        handleDataChange={handleDataChange}
        featureFlagLocationFilter={true}
      />
    </>
  );
};

FiltersSection.propTypes = filtersPropType;

export default memo(FiltersSection, isEqual);
